import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import useApiCall from 'hooks/use-api-call';
import { AdminInvestmentList, AdminInvestmentDetails, InvestmentCancellationEnum } from 'api/models';
import { AdminApi, AdminInvestmentsListOrderingEnum } from 'api/apis';
import Translate from 'ui/atoms/translate';
import StudioConfirmInvestmentsTable from 'apps/issuer/shared/confirm-investments-table-view/confirm-investments-table';
import IssuerBatchConfirmation from './batch-confirmation';
import WithDataRecord from 'hoc/WithDataRecord';
import HeaderPanel from 'ui/atoms/header-panel';
import IssuerCreditSecurities from './credit-securities';
import InvestmentCancellationModal, {
  CancellationModalType,
} from 'src/apps/issuer/shared/investment-cancellation-modal';
import WideContent from 'core/layout/content/wide-content';
import { AdminInvestmentsListStatusEnum } from 'api';
import { useIssuerOverview } from 'apps/issuer/hooks';
import { ConfirmInvestmentsTypes } from 'apps/issuer/shared/confirm-investments-table-view/types';
import useIsMedia, { DEVICES } from 'src/ui/hooks/use-is-media';
import Spacer from 'src/ui/atoms/spacer';
import { hasDistributionViewDataPermission } from '../../helpers/distributionPlatformPermissions';
import {
  hasManageApprovalsPermission,
  hasManageCreditDigitalSecuritiesPermission,
  hasManageIncomingPaymentsPermission,
  hasViewPermission,
} from '../../helpers/tokenPermissions';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import PageWrapper from 'src/core/layout/content/page-wrapper';

interface IssuerInvestmentsCornfirmationTableProps {
  type: ConfirmInvestmentsTypes;
  defaultLimit?: number;
  setFilterPopupHeight?: (height: number) => void;
}

const IssuerInvestmentsCornfirmationTable: FunctionComponent<IssuerInvestmentsCornfirmationTableProps> = WithDataRecord<
  AdminApi,
  AdminInvestmentList
>(AdminApi)(
  ({ data, ordering, onOrderBy, type, loadData, loading, paginationProps, setFilterPopupHeight }) => {
    const [loadingConfirmationId, setLoadingConfirmationId] = useState<string>();
    const [investmentForIssuance, setInvestmentForIssuance] = useState<AdminInvestmentDetails>();
    const [investmentForCancellationConfirmation, setInvestmentForCancellationConfirmation] = useState<string>();
    const [errorCancellation, setErrorCancellation] = useState(false);
    const [successCancellation, setSuccessCancellation] = useState(false);

    const isPhone = useIsMedia(DEVICES.phone);
    const { currentUser } = useCurrentUserSelector();
    const { withApi, makeAuthenticatedApi } = useApiCall();

    const {
      withApi: withCancellationApi,
      makeAuthenticatedApi: makeAuthenticatedCancellationApi,
      loading: loadingCancellation,
    } = useApiCall();

    const investmentsApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

    const investmentsCancellationApi: AdminApi = useMemo(
      () => makeAuthenticatedCancellationApi(AdminApi),
      [makeAuthenticatedCancellationApi],
    );

    const tokensApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

    const { loading: loadingOpenActions } = useIssuerOverview();

    const onConfirm = useCallback(
      (investmentId: string) => {
        if (type === 'approveOffers') {
          withApi(async () => {
            setLoadingConfirmationId(investmentId);
            await investmentsApi.adminInvestmentsOfferApprovalCreate({
              id: investmentId,
            });
            loadData();
            setLoadingConfirmationId(undefined);
          });
        }
        if (type === 'confirmPayments') {
          withApi(async () => {
            setLoadingConfirmationId(investmentId);
            await investmentsApi.adminInvestmentsPaymentConfirmationCreate({
              id: investmentId,
            });
            loadData();
            setLoadingConfirmationId(undefined);
          });
        }
        if (type === 'creditSecurities') {
          withApi(async () => {
            setLoadingConfirmationId(investmentId);
            const investment = await investmentsApi.adminInvestmentsRetrieve({
              id: investmentId,
            });
            setInvestmentForIssuance(investment);
            loadData();
            setLoadingConfirmationId(undefined);
          });
        }
      },
      [withApi, investmentsApi, tokensApi, loadData, type],
    );

    const cancellationReason = useMemo(() => {
      if (type === 'approveOffers') return InvestmentCancellationEnum.REJECTION;
      if (type === 'confirmPayments') return InvestmentCancellationEnum.PAYMENT_MISSING;
      return undefined;
    }, [type]);

    const onCancelInvestment = useCallback(
      (investmentId: string) => {
        if (!cancellationReason) return;
        return withCancellationApi(async () => {
          try {
            await investmentsCancellationApi.adminInvestmentsCancellationCreate({
              id: investmentId,
              investmentCancellationRequest: {
                reason: cancellationReason,
              },
            });
            setSuccessCancellation(true);
            loadData();
          } catch (e) {
            setErrorCancellation(true);
            console.error(e);
          }
        });
      },
      [withCancellationApi, investmentsCancellationApi, loadData, cancellationReason],
    );

    useEffect(() => {
      if (investmentForCancellationConfirmation) {
        setSuccessCancellation(false);
        setErrorCancellation(false);
      }
    }, [investmentForCancellationConfirmation]);

    const filteredData = useMemo(() => {
      if (type === 'creditSecurities') return data?.filter(({ canIssue }) => canIssue);

      return data;
    }, [data, type]);

    const hasApprovalPermissions =
      hasDistributionViewDataPermission(currentUser?.associatedDistributionPlatforms || []) ||
      (hasViewPermission(currentUser) && hasManageApprovalsPermission(currentUser));
    const hasConfirmPaymentsPermissions =
      hasDistributionViewDataPermission(currentUser?.associatedDistributionPlatforms || []) ||
      (hasViewPermission(currentUser) && hasManageIncomingPaymentsPermission(currentUser));
    const hasCreditSecuritiesPermissions =
      hasDistributionViewDataPermission(currentUser?.associatedDistributionPlatforms || []) ||
      (hasViewPermission(currentUser) && hasManageCreditDigitalSecuritiesPermission(currentUser));

    const permissionMap: Record<ConfirmInvestmentsTypes, boolean> = {
      approveOffers: hasApprovalPermissions,
      confirmPayments: hasConfirmPaymentsPermissions,
      creditSecurities: hasCreditSecuritiesPermissions,
    };
    const hasPermission = permissionMap[type];

    return (
      <>
        <HeaderPanel title={<Translate name={`studioConfirmInvestmentsTable.${type}.title`} />}>
          {hasPermission && !isPhone && (type === 'approveOffers' || type === 'confirmPayments') && (
            <IssuerBatchConfirmation type={type} investments={filteredData} onReloadData={loadData} />
          )}
        </HeaderPanel>
        {hasPermission && isPhone && (type === 'approveOffers' || type === 'confirmPayments') && (
          <>
            <IssuerBatchConfirmation type={type} investments={filteredData} onReloadData={loadData} />
            <Spacer y={2} />
          </>
        )}
        <StudioConfirmInvestmentsTable
          type={type}
          ordering={ordering}
          onOrderBy={onOrderBy}
          loading={loading || loadingOpenActions}
          loadingConfirmationId={loadingConfirmationId}
          onConfirm={onConfirm}
          onOpenCancellationModal={setInvestmentForCancellationConfirmation}
          investments={filteredData}
          paginationProps={paginationProps}
          hasPermission={hasPermission}
          setFilterPopupHeight={setFilterPopupHeight}
        />
        {investmentForCancellationConfirmation && (
          <InvestmentCancellationModal
            onCancelInvestment={() => onCancelInvestment(investmentForCancellationConfirmation)}
            loading={loadingCancellation}
            onClose={() => setInvestmentForCancellationConfirmation(undefined)}
            successCancellation={successCancellation}
            errorCancellation={errorCancellation}
            type={
              cancellationReason === InvestmentCancellationEnum.REJECTION
                ? CancellationModalType.REJECTION
                : CancellationModalType.KYC_OR_PAYMENT_MISSING
            }
          />
        )}
        {type === 'creditSecurities' && investmentForIssuance && (
          <IssuerCreditSecurities
            investment={investmentForIssuance}
            onDone={() => {
              loadData();
              setInvestmentForIssuance(undefined);
            }}
          />
        )}
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminInvestmentsList({
      offset,
      limit,
      ordering: ordering as AdminInvestmentsListOrderingEnum,
      status: {
        approveOffers: AdminInvestmentsListStatusEnum.ACCEPTABLE,
        confirmPayments: AdminInvestmentsListStatusEnum.PAYMENT_OUTSTANDING,
        creditSecurities: AdminInvestmentsListStatusEnum.SECURITIES_OUTSTANDING,
      }[props.type],
      ...props,
    });
  },
);

const IssuerConfirmInvestmentsTableView: FunctionComponent<{
  type: ConfirmInvestmentsTypes;
  defaultLimit?: number;
}> = ({ type, defaultLimit }) => {
  const [filterPopupHeight, setFilterPopupHeight] = useState(0);

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <WideContent>
        <IssuerInvestmentsCornfirmationTable
          type={type}
          defaultLimit={defaultLimit}
          setFilterPopupHeight={setFilterPopupHeight}
        />
      </WideContent>
    </PageWrapper>
  );
};

export default IssuerConfirmInvestmentsTableView;
